// Profil-Nachbau F-09 (Bennys Freigabe 14.10.2026, BAU-PRAEFERENZEN.md): Wörter, Typen und Regeln der Seite „Präferenzen“
// ohne React, damit frontend/tests sie ohne Browser prüfen. Die Werte kommen vom Server (GET /api/auth/preferences); die
// Seite entscheidet nichts selbst, was der Server entscheidet (Firmensperre, Recherche-Art). Der Produktname kommt vom
// Aufrufer (brand.displayName, nach MK-1 immer CLAiRIS), wie überall kein fester Name im Baustein.
import type { ThemeChoice } from '../../utils/theme';

export type HinweisGrund = 'SCHRIFTSATZ' | 'REGELN_AUFHEBEN' | 'FREMDE_QUELLEN';

/** Antwort von GET /api/auth/preferences (BAU-PRAEFERENZEN.md 2.3). */
export interface PraeferenzenStand {
  webSearch: { eigene: boolean | null; wirksam: boolean; gesperrtVon: string | null };
  sendWithEnter: boolean;
  colorScheme: ThemeChoice | null;
  hints: { vorhanden: boolean; laenge: number; vorschau: string | null; aktualisiert: string | null; revision: number };
  infoMail: boolean;
  recherche: { websucheSchaltbar: boolean; hinweiseMoeglich: boolean };
}

export const HINWEISE_HOECHSTENS = 1000;
/** Ab dieser Länge steht der Zähler in der Warnfarbe. */
export const HINWEISE_WARNUNG_AB = 900;
export const WEBSUCHE_ANKER = 'websuche';
export const HINWEISE_ANKER_PRAEFERENZEN = 'hinweise';
export const ENTER_ANKER = 'enter';
export const DARSTELLUNG_ANKER = 'darstellung';

// Tausenderpunkt wie in der Freigabe („1.000 Zeichen“); Intl mit de-AT setzt ein schmales Leerzeichen.
const zahl = (n: number) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '.');

export const PRAEFERENZEN_TEXTE = {
  titel: 'Präferenzen',
  // F-24 (PF-TX-rahmen-5): Unterzeile wie meinGPT, nennt „Darstellung“ (Nutzerblick Z-02).
  beschreibung: 'Recherche und Darstellung, für Ihr Konto auf allen Geräten.',
  laden: 'Präferenzen werden geladen …',
  ladeFehler: 'Die Präferenzen konnten nicht geladen werden.',
  erneut: 'Erneut versuchen',
  gespeichert: 'Gespeichert.',
  speicherFehler: 'Die Einstellung konnte nicht gespeichert werden.',
  nichtVerfuegbar: 'In dieser Recherche-Art nicht verfügbar.',
  recherche: 'Recherche',
  darstellung: 'Darstellung',
  websuche: 'Websuche',
  websucheErklaerung: (produkt: string) => `Wenn an, sucht ${produkt} neben RIS und Kollektivverträgen auch im Web.`,
  websucheGoogle: 'Dabei gehen aus Ihrer Frage gebildete Suchbegriffe an die Google-Suche.',
  websucheKennzeichnung: 'Fundstellen aus dem Web sind in der Antwort als „laut Webrecherche, im RIS nicht verifiziert“ gekennzeichnet.',
  websucheGesperrt: (firma: string) => `${firma} hat die Websuche für alle abgeschaltet.`,
  // F-23 (Nutzerblick Mitglied H-01; Wortlaut SPEC-recht-b Abschnitt 3): der Hinweis unter dem Eingabefeld der Startseite und der
  // leeren Recherche, nur bei wirksam eingeschalteter Websuche (pages/websucheHinweis.tsx).
  websucheHinweis: 'Websuche an: Aus Ihrer Frage gebildete Suchbegriffe gehen an die Google-Suche.',
  websucheHinweisLink: 'In den Präferenzen ändern',
  hinweise: 'Persönliche Hinweise',
  hinweiseErklaerung: (produkt: string) => `Was ${produkt} bei jeder Recherche zusätzlich beachten soll.`,
  hinweiseTipp: (produkt: string) => `Die Hinweise ergänzen die Regeln von ${produkt} und Ihrer Firma, sie ersetzen keine.`,
  keineHinweise: 'Keine Hinweise',
  bearbeiten: 'Hinweise bearbeiten',
  beispiele: 'Zum Beispiel: „Antworten Sie kurz.“, „Ich arbeite vor allem im Arbeitsrecht.“, „Nennen Sie immer die Paragraphen mit Fassung.“',
  mandantendaten: 'Keine Mandantendaten eintragen. Die Hinweise gehen mit jeder Frage an die KI.',
  // F-24 (PF-TX-rahmen-5, Nutzerblick Z-05): das Symbol am Ende des Satzes; das Wort steht hier und nicht in Praeferenzen.tsx, weil
  // K12-4 (static-brand-fonts.test.mjs) „Mandantendaten“ nur in den freigegebenen Textdateien erlaubt. Ohne „Mandantinnen und
  // Mandanten“, gleicher Wortlaut wie T-M in SPEC-hilfe-b.
  mandantendatenWort: 'Mandantendaten',
  mandantendatenTipp: 'Namen und Angaben Ihrer Klientschaft, also Ihrer Kundinnen und Kunden.',
  zaehler: (n: number) => `${zahl(n)} von ${zahl(HINWEISE_HOECHSTENS)} Zeichen`,
  speichern: 'Hinweise speichern',
  abbrechen: 'Abbrechen',
  loeschen: 'Hinweise löschen',
  loeschenFrage: 'Hinweise löschen?',
  loeschenSatz: 'Neue Recherchen laufen ohne Ihre persönlichen Hinweise.',
  loeschenKnopf: 'Löschen',
  pruefen: 'Hinweise werden geprüft …',
  hinweiseGespeichert: 'Hinweise gespeichert. Sie gelten ab der nächsten Frage.',
  hinweiseGeloescht: 'Hinweise gelöscht.',
  enter: 'Mit Enter senden',
  enterErklaerung: 'An: Enter sendet die Frage, Shift + Enter macht eine neue Zeile. Aus: Enter macht eine neue Zeile, gesendet wird mit Shift + Enter.',
  // F-23 (Fehlersuche Nr. 12): der Satz neben dem Schalter am Eingabefeld der Recherche, je nach Stellung (die zwei Hälften von
  // enterErklaerung).
  enterAn: 'Enter sendet die Frage, Shift + Enter macht eine neue Zeile.',
  enterAus: 'Enter macht eine neue Zeile, gesendet wird mit Shift + Enter.',
  enterZusatz: 'Am Eingabefeld der Recherche schalten Sie dasselbe mit „Mit Enter senden“ um.',
  farbschema: 'Farbschema',
  farbschemaTipp: '„System“ folgt der Einstellung des jeweiligen Geräts.',
} as const;

const GRUENDE: Record<HinweisGrund, (produkt: string) => string> = {
  SCHRIFTSATZ: () => 'Sie verlangen das Verfassen von Schriftsätzen, Verträgen oder Mustertexten.',
  REGELN_AUFHEBEN: produkt => `Sie sollen Regeln von ${produkt} aufheben oder den Systemtext ausgeben.`,
  FREMDE_QUELLEN: () => 'Sie verlangen andere Quellen als RIS, Kollektivverträge und Websuche.',
};

/** Der Satz zu einer Ablehnung der Hinweise; der Code kommt vom Server (BAU-PRAEFERENZEN.md 2.3, 2.4). null: kein eigener
 *  Satz, dann gilt der Text des Servers bzw. errorMessage. */
export function hinweisFehler(code: string | undefined, produkt: string, grund?: string): string | null {
  switch (code) {
    case 'HINTS_TOO_LONG': return `Höchstens ${zahl(HINWEISE_HOECHSTENS)} Zeichen.`;
    case 'HINTS_INVALID_CHARS': return 'Der Text enthält unsichtbare Steuerzeichen. Bitte tippen Sie ihn neu ein.';
    case 'HINTS_REJECTED': return grund && grund in GRUENDE ? `Diese Hinweise übernimmt ${produkt} nicht: ${GRUENDE[grund as HinweisGrund](produkt)}` : null;
    case 'HINTS_CHECK_UNAVAILABLE': return 'Die Prüfung ist gerade nicht erreichbar. Ihre Hinweise sind nicht gespeichert; bitte versuchen Sie es in einigen Minuten erneut.';
    case 'LANE_NOT_SUPPORTED': return PRAEFERENZEN_TEXTE.nichtVerfuegbar;
    default: return null;
  }
}

/** Wert rechts in der Zeile: die ersten 60 Zeichen und „…“, sonst „Keine Hinweise“. */
export function hinweisVorschau(vorschau: string | null | undefined, laenge: number): string {
  if (!vorschau) return PRAEFERENZEN_TEXTE.keineHinweise;
  return laenge > 60 ? `${vorschau.slice(0, 60)}…` : vorschau;
}

/** Tastenregel der Eingabefelder (F-09, BAU-PRAEFERENZEN.md 1.4): sendet die Taste die Frage? Während einer Eingabe mit
 *  Eingabemethode (isComposing) nie. */
export function enterSendet(taste: { key: string; shiftKey: boolean; isComposing?: boolean }, mitEnterSenden: boolean): boolean {
  if (taste.key !== 'Enter' || taste.isComposing) return false;
  return mitEnterSenden ? !taste.shiftKey : taste.shiftKey;
}

/** Der Browserschlüssel des Schalters am Eingabefeld vor F-09 („Chat-Modus“/„Schreib-Modus“). */
export const CHAT_MODUS_SCHLUESSEL = 'risearch-chat-mode';

/** Die frühere Wahl im Browser oder null (keine Wahl, gesperrter Speicher, kein Browser). */
export function alterChatModus(): boolean | null {
  try {
    const wert = window.localStorage.getItem(CHAT_MODUS_SCHLUESSEL);
    return wert === 'true' ? true : wert === 'false' ? false : null;
  } catch { return null; }
}

/** Wirksamer Wert von „Mit Enter senden“ an den Eingabefeldern (BAU-PRAEFERENZEN.md 1.4, F-P3): die Wahl am Konto; hat das Konto
 *  noch keine (null) oder fehlt das Feld (undefined), die frühere Wahl im Browser; sonst aus. */
export function mitEnterSenden(konto: boolean | null | undefined, browser: boolean | null): boolean {
  return konto ?? browser ?? false;
}

/** Übergang (BAU-PRAEFERENZEN.md 1.4): der Wert, den die Oberfläche einmal an den Server schreibt – nur wenn das Konto noch keine
 *  Wahl hat (null) und der Browser eine kennt. Danach gilt nur noch das Konto. */
export function chatModusUebernahme(konto: boolean | null | undefined, browser: boolean | null): boolean | undefined {
  return konto === null && browser !== null ? browser : undefined;
}
